"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { clientCookies } from "@/lib/clientCookies";
import { useEcomStore } from "@/context/ecom/EcomContext";
import { useLogin } from "@/context/login/LoginContext";
import { tidProfilesBaseUrl } from "@/lib/config/environments";
import { refreshAuthToken } from "@/lib/refreshAuthToken";
import profile from "@/profiles/profileSingleton";

/**
 * Syncs the current locale with the user's TID profile language and a
 * persistence cookie.  Terra's LanguageSelector handles navigation — this
 * hook only reacts to the resulting URL-lang change.
 */
type SiteLang = {
  code?: string;
  localeCode?: string;
  nativeField?: string;
  translatableName?: string;
};
const useLanguage = (lang: string, siteLangs: SiteLang[] | undefined, uuid?: string, client_id?: string, redirect_uri?: string, code_verifier?: string, url?: string) => {
  const router = useRouter();
  const [state] = useEcomStore() ?? [];
  const loginCtx = useLogin();
  const [loginState, loginDispatch] = loginCtx ?? [];
  const onLogin = useCallback((token: string, authTime: string, idToken: string, refreshToken: string) => {
    loginDispatch?.({
      type: "SET_LOGIN",
      payload: {
        token,
        authTime,
        idToken,
        refreshToken
      }
    });
  }, [loginDispatch]);
  const onLogout = useCallback(() => {
    loginDispatch?.({
      type: "LOG_OUT"
    });
  }, [loginDispatch]);
  const [profileLang, setProfileLang] = useState<string | null>(null);

  // Flips to `true` when the sync effect detects a logged-in user with a
  // stale cookie — i.e. they explicitly switched language this mount.
  // The redirect effect respects this and won't bounce the user back to
  // their stored profile language.
  const explicitSwitchThisMountRef = useRef(false);
  // Ensures the profile-language redirect runs at most once per mount.
  const hasAppliedProfileLangRef = useRef(false);

  // Converts the nextJS locale code ("en") to the full language code ("en-US") that the profile API (TID) expects.
  const toTIDLocale = useCallback((lang_0: string): string => {
    const match = siteLangs?.find(l => l?.code?.toLowerCase() === lang_0?.toLowerCase());
    return match?.localeCode || lang_0;
  }, [siteLangs]);

  // The exact opposite: converts the full language code back to the short URL code. 
  // If the profile code doesn't match a language this site actually supports, we just return undefined and leave the URL alone.
  const toSiteLocale = useCallback((localeCode: string | null | undefined): string | undefined => {
    if (!localeCode) return undefined;
    const match_0 = siteLangs?.find(l_0 => l_0?.localeCode?.toLowerCase() === localeCode.toLowerCase());
    return match_0?.code;
  }, [siteLangs]);
  const updateProfileLanguage = useCallback(async (languageCode: string, accessTokenOverride?: string | null) => {
    const token_0 = accessTokenOverride ?? loginState?.token;
    if (token_0 && uuid && languageCode) {
      return fetch(`${tidProfilesBaseUrl}/users/${uuid}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token_0}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          language: languageCode
        })
      }).then(response => {
        if (response.ok) {
          setProfileLang(languageCode);
        }
        if (response?.status === 401) {
          refreshAuthToken(loginState?.refreshToken ?? null, client_id || "", redirect_uri || "", code_verifier || "", null as any, null as any, null as any, (newToken: string | null) => updateProfileLanguage(languageCode, newToken ?? null), null as any, onLogin, onLogout);
        }
      }).catch(error => {
        // eslint-disable-next-line no-console
        console.log("error", error);
      });
    }
  }, [uuid, client_id, redirect_uri, code_verifier, url, onLogin, onLogout, loginState?.token, loginState?.refreshToken]);

  // Fetch TID profile language once auth completes
  useEffect(() => {
    if (profile.NEXT_PUBLIC_DISABLE_LANGUAGE_HANDLING) return;
    if (!loginState?.token) return;
    fetch(`${tidProfilesBaseUrl}/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${loginState.token}`,
        "Content-Type": "application/json"
      }
    }).then(r => r.json()).then(data => setProfileLang(data?.language)).catch(error_0 => {
      // eslint-disable-next-line no-console
      console.log("error", error_0);
    });
  }, [state?.isAuthCheckComplete, loginState?.token]);

  // Keep the `languageCode` cookie and the TID profile in sync with the URL
  // `lang`. Runs on mount and on any lang/auth change.
  //
  // - User switched language while logged in (Cookie != URL): 
  //   Terra reloaded the page before we could catch it. Save the new language to their 
  //   profile and flag it so our redirect logic doesn't mess with them.
  //
  // - User switched language while logged out (Cookie != URL): 
  //   Just update the cookie to match. If we don't do this, their casual browsing while 
  //   logged out will look like a deliberate profile update when they log back in.
  useEffect(() => {
    if (profile.NEXT_PUBLIC_DISABLE_LANGUAGE_HANDLING) return;
    if (!state?.isAuthCheckComplete) return;
    const cookieLang = clientCookies.get("languageCode");
    if (loginState?.token && uuid && cookieLang && cookieLang !== lang) {
      explicitSwitchThisMountRef.current = true;
      updateProfileLanguage(toTIDLocale(lang));
    }
    if (cookieLang !== lang) {
      clientCookies.set("languageCode", lang, {
        expires: 365
      });
    }
  }, [lang, uuid, loginState?.token, state?.isAuthCheckComplete, updateProfileLanguage, toTIDLocale]);

  // Apply the user's TID profile language to the URL. 
  useEffect(() => {
    if (profile.NEXT_PUBLIC_DISABLE_LANGUAGE_HANDLING) return;
    if (!loginState?.token || !uuid) return;
    if (!profileLang) return;
    if (hasAppliedProfileLangRef.current) return;
    if (explicitSwitchThisMountRef.current) {
      hasAppliedProfileLangRef.current = true;
      return;
    }
    const profilePublicCode = toSiteLocale(profileLang);
    // siteLangs may not be populated yet on the first render where
    // profileLang arrived. Leave the ref un-set so we re-evaluate.
    if (!profilePublicCode) return;
    if (profilePublicCode.toLowerCase() === lang.toLowerCase()) {
      hasAppliedProfileLangRef.current = true;
      return;
    }
    hasAppliedProfileLangRef.current = true;
    if (typeof window === "undefined") return;
    const restOfPath = window.location.pathname.replace(/^\/[^/]+/, "");
    router.replace(`/${profilePublicCode}${restOfPath}${window.location.search}`);
  }, [profileLang, lang, uuid, loginState?.token, toSiteLocale, siteLangs, router]);
  return {
    profileLang
  };
};
export default useLanguage;